import { Jaco } from "oc-jaco3d/arrangement/Jaco";
import { AbschnittRuler3d } from "../arrangement3d/AbschnittRuler3d";

export type RulerStyle =
  | "Segment"
  | "Leerraum"
  | "Empty"
  | "GanzesSegment"
  | "Element"
  | "Tor"
  | "Überstand"
  | "Schiebetorüberstand"
  | "";

/**
 * Höhe der Maß-Textlabels in 3d-Einheiten. Muss mit fontHeight in AbschnittRuler3d übereinstimmen.
 * Der Z-Stapelabstand (RULER_STACK_STEP) muss größer sein, damit sich Labels nicht überlagern.
 */
export const RULER_FONT_HEIGHT = 0.2;

/** Z-Offset der untersten (kameranächsten) Stapelebene. */
export const RULER_BASE_VERSATZ = 0.7;

/**
 * Z-Abstand zwischen zwei benachbarten Stapelebenen. Bewusst größer als RULER_FONT_HEIGHT,
 * damit sich die Textlabels unterschiedlicher Ruler-Typen niemals überlagern.
 */
export const RULER_STACK_STEP = 0.3;

/**
 * Typ-Reihenfolge, die nur noch als Tie-Break beim Stapeln dient (innen/kameranah -> außen).
 * Haben zwei sich überlappende Ruler dieselbe Länge, bekommt der Typ weiter vorne in dieser
 * Liste den geringeren Z-Offset.
 */
export const RULER_STACK_PRIORITY: RulerStyle[] = [
  "Empty",
  "Leerraum",
  "Element",
  "Tor",
  "Schiebetorüberstand",
  "Segment",
  "Überstand",
  "GanzesSegment",
];

export interface RulerLayoutInput {
  x0: number;
  x1: number;
  style: RulerStyle;
}

/**
 * Berechnet pro Ruler den gestapelten Z-Offset (versatz), aligniert zur Eingabereihenfolge.
 *
 * Regeln:
 * - Sich in x überlappende Ruler müssen auf unterschiedlichen Z-Ebenen liegen (Stacking,
 *   unabhängig vom Typ). Nicht überlappende Ruler dürfen sich eine Ebene teilen.
 * - Sortierung: der kürzere Ruler (kleineres delta x) bekommt den geringeren Z-Offset, bei
 *   gleicher Länge entscheidet die Typ-Priorität (RULER_STACK_PRIORITY).
 * - Liegt nur ein Ruler / liegen keine Überlappungen vor, bekommen alle den kleinsten Offset.
 */
export function computeRulerVersatz(rulers: RulerLayoutInput[], epsilon = 0.01): number[] {
  const items = rulers.map((r, index) => {
    const a = Math.min(r.x0, r.x1);
    const b = Math.max(r.x0, r.x1);
    const prio = RULER_STACK_PRIORITY.indexOf(r.style);
    return {
      index,
      a,
      b,
      length: b - a,
      typePrio: prio < 0 ? RULER_STACK_PRIORITY.length : prio,
    };
  });

  // kürzester Ruler zuerst -> geringster Z-Offset; bei Gleichstand nach Typ-Priorität
  const order = [...items].sort(
    (p, q) => p.length - q.length || p.typePrio - q.typePrio || p.index - q.index
  );

  // Greedy: jeder Ruler bekommt die kleinste Ebene, auf der er sich mit keinem bereits
  // platzierten Ruler in x überschneidet.
  const levels: { a: number; b: number }[][] = [];
  const levelByIndex = new Array<number>(rulers.length);
  for (const item of order) {
    let level = 0;
    for (; level < levels.length; level++) {
      const überlappt = levels[level].some(
        (placed) => Math.min(item.b, placed.b) - Math.max(item.a, placed.a) > epsilon
      );
      if (!überlappt) break;
    }
    if (level === levels.length) {
      levels.push([]);
    }
    levels[level].push({ a: item.a, b: item.b });
    levelByIndex[item.index] = level;
  }

  return levelByIndex.map((level) => RULER_BASE_VERSATZ + level * RULER_STACK_STEP);
}

export class AbschnittRuler extends Jaco {
  static CLASS_NAME = "AbschnittRuler";
  isRuler = true;
  x0 = 0;
  x1: number = 0;
  style: RulerStyle = "";
  /** gestapelter Z-Offset, wird in Segment.updateRuler gesetzt */
  versatz = RULER_BASE_VERSATZ;

  constructor(parent: Jaco) {
    super(parent);
  }

  createJaco3d(): AbschnittRuler3d {
    return new AbschnittRuler3d(this);
  }
}
